import React from 'react';
import { StepComponentProps, StepDefinition } from './StepDefinition';
import { CatalogueFacade } from 'pos/facades';
import { StepLoader } from './StepLoader';
import * as SalesStore from '../../SalesStore';
import { ColorDTO, Res, SizeDTO } from 'core/api';
import { Message } from 'core/ui-next/modal';
import { VStack, Text, HStack, Separator, Box, List } from '@chakra-ui/react';
import { useAutoAdvance } from './useStepAutoAdvance';
import { Colors } from 'core/ui';
import { ThemeManager } from 'pos/ui';
import { CompositionState } from './Composition';
import { useCompositionSetters } from './useCompositionSetters';


async function validate({ size, color }: CompositionState): Promise<boolean> {
	if (size.Id === 0 || color.Id === 0) {
		await Message.show(Res.Msg.MSG_SIZE_AND_COLOR_MUST_BE_SELECTED());
		return false;
	}

	return true;
}

function clean(composition: CompositionState): CompositionState {
	return { ...composition, size: { Id: 0, Name: '', Priority: 0 }, color: { Id: 0, Name: '', Value: '', Priority: 0 } };
}

const SizeAndColorComposition = (props: StepComponentProps & { sizes: SizeDTO[], colors: ColorDTO[] }) => {

	const { sizes, colors, setComposition, composition, status, requestNext } = props;
	const { setColor, setSize } = useCompositionSetters(setComposition);
	const isCompleted = composition.size.Id !== 0 && composition.color.Id !== 0;

	const { enableAutoAdvance } = useAutoAdvance({
		status,
		isCompleted,
		onAutoAdvance: requestNext
	});

	const onSelectColor = React.useCallback(async (color: ColorDTO) => {
		enableAutoAdvance();
		setColor(color);
	}, [enableAutoAdvance, setColor]);

	const onSelectSize = React.useCallback(async (size: SizeDTO) => {
		enableAutoAdvance();
		setSize(size);
	}, [enableAutoAdvance, setSize]);


	return <VStack width={'full'} height={'full'} gap={'10'}>
		<VStack width={'full'} gap={'4'} alignItems={'flex-start'}>
			<Text fontSize={'md'} fontWeight={'semibold'}>{Res.Web.COMPOSE_PRODUCT_SIZE_AND_COLOR_LBL()}</Text>
			<HStack width={'full'} gap={ThemeManager.isLargeDevice() ? '6' : '3'} alignItems={'flex-start'}>
				<Box flex={1} display={'flex'} flexDirection={'column'} gap={'0'}>
					<Text fontSize={'sm'} fontWeight={'semibold'} paddingX={'4'} paddingY={'3'} borderBottom={'1px solid'} borderBottomColor={'neutral.300'}>{Res.Web.COMPOSE_PRODUCT_SIZE_LBL()}</Text>
					<List.Root margin={'0'} overflow={'auto'} height={ThemeManager.isLargeDevice() ? '92.5' : '87.5'}>
						{sizes.map((x, index) =>
							<List.Item
								key={x.Id}
								width={'full'}
								height={'10'}
								paddingX={'4'}
								paddingY={'3'}
								display={'flex'}
								alignItems={'center'}
								backgroundColor={x.Id === composition.size.Id ?
									{ base: 'blue.100', _dark: 'blue.600' }
									: {
										base: index % 2 !== 0 ?
											'white' : 'neutral.50', _dark: index % 2 !== 0 ? 'neutral.800' : 'neutral.700'
									}}
								borderBottom={index % 2 !== 0 ? 'none' : '1px solid'}
								borderBottomColor={index % 2 !== 0 ? 'transparent' : 'neutral.100'}
								cursor={'pointer'}
								_hover={{
									backgroundColor: x.Id === composition.size.Id ?
										{ base: 'blue.100', _dark: 'blue.600' }
										: { base: 'blue.50', _dark: 'blue.500' }
								}}
								onClick={() => onSelectSize(x)}
							>
								<Text>{x.Name}</Text>
							</List.Item>
						)}
					</List.Root>
				</Box>
				<Separator height={'full'} orientation={'vertical'} borderColor={'transparent'} bg={{ base: '{colors.neutral.300}', _dark: '{colors.neutral.300}' }} width={'1.25px'} />
				<Box flex={1} display={'flex'} flexDirection={'column'} gap={'0'}>
					<Text fontSize={'sm'} fontWeight={'semibold'} paddingX={'4'} paddingY={'3'} borderBottom={'1px solid'} borderBottomColor={'neutral.300'}>{Res.Web.COMPOSE_PRODUCT_COLOR_LBL()}</Text>
					<List.Root margin={'0'} overflow={'auto'} height={ThemeManager.isLargeDevice() ? '92.5' : '87.5'}>
						{colors.map((x, index) =>
							<List.Item
								key={x.Id}
								width={'full'}
								height={'10'}
								paddingX={'4'}
								paddingY={'3'}
								display={'flex'}
								alignItems={'center'}
								backgroundColor={x.Id === composition.color.Id ?
									{ base: 'blue.100', _dark: 'blue.600' }
									: {
										base: index % 2 !== 0 ?
											'white' : 'neutral.50', _dark: index % 2 !== 0 ? 'neutral.800' : 'neutral.700'
									}}
								borderBottom={index % 2 !== 0 ? 'none' : '1px solid'}
								borderBottomColor={index % 2 !== 0 ? 'transparent' : 'neutral.100'}
								cursor={'pointer'}
								_hover={{
									backgroundColor: x.Id === composition.color.Id ?
										{ base: 'blue.100', _dark: 'blue.600' }
										: { base: 'blue.50', _dark: 'blue.500' }
								}}
								onClick={() => onSelectColor(x)}
							>
								{x.Value ? <Box minWidth={'4'} minHeight={'4'} borderRadius={'sm'} backgroundColor={Colors.getBackgroundColorAsRgb(x.Value)} marginRight={'2'} /> : null}
								<Text>{x.Name}</Text>
							</List.Item>
						)}
					</List.Root>
				</Box>
			</HStack>
		</VStack>

	</VStack>;
};

export const Component = (props: StepComponentProps) => {

	const getData = React.useCallback(async (composition: CompositionState) => {

		const selectedProduct = composition.selectedProduct;
		if (selectedProduct === null)
			throw new Error('Product has not been selected');

		if (selectedProduct.Barcode !== '') {
			const sizeAndColorByBarcode = await CatalogueFacade.parseBarcode(selectedProduct!.Barcode, SalesStore.getTicket().Id);
			if (sizeAndColorByBarcode.Products[0].Size.Id !== 0 && sizeAndColorByBarcode.Products[0].Color.Id !== 0)
				return { sizes: [sizeAndColorByBarcode.Products[0].Size], colors: [sizeAndColorByBarcode.Products[0].Color] };
		}

		const sizesAndColors = await CatalogueFacade.getSizesAndColorsByProductId(selectedProduct.Id);
		if (sizesAndColors.Colors.length === 0 || sizesAndColors.Sizes.length === 0) {
			await Message.show(Res.Msg.MSG_PRODUCT_WITHOUT_SIZES_OR_COLORS());
		}

		return { sizes: sizesAndColors.Sizes, colors: sizesAndColors.Colors };

	}, []);


	const getNextComposition = React.useCallback(({ data, composition }: { data: Awaited<ReturnType<typeof getData>>; composition: CompositionState; }) => {

		const canBeCompletedAutomatically = data.sizes.length === 1 && data.colors.length === 1;
		if (!canBeCompletedAutomatically) {
			return composition;
		}

		const size = data.sizes[0];
		const color = data.colors[0];

		const sameSize = composition.size.Id === size.Id;
		const sameColor = composition.color.Id === color.Id;

		if (sameSize && sameColor) {
			return composition;
		}

		return { ...composition, size, color };
	}, []);

	return (
		<StepLoader {...props} getData={getData} getNextComposition={getNextComposition}>
			{(props) => (<SizeAndColorComposition{...props} />)}
		</StepLoader>
	);
};

const step: StepDefinition = {
	id: 'SizeAndColor',
	label: Res.Web.WIZARD_COMPOSE_SELECT_SIZE_AND_COLOR(),
	canFinish: true,
	useKeyPad: false,
	validate,
	clean,
	Component
};

export default step;
